/**
 * Schedule Page - Sprint 4 + Sprint 11A Layout Migration
 * Lists visits with filtering, create visit modal
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { visitsAPI, clientsAPI, usersAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { RefreshCw, Plus, Clock, User, FileCheck, DollarSign, AlertTriangle, ExternalLink, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  Scheduled: 'bg-avida-blue-100 text-avida-blue-700',
  InProgress: 'bg-amber-100 text-amber-700',
  Completed: 'bg-emerald-100 text-emerald-700',
  Cancelled: 'bg-slate-100 text-slate-600',
  NoShow: 'bg-red-100 text-red-700'
};

const SERVICE_TYPES = [
  'Personal Care',
  'Homemaker',
  'Respite Care',
  'Supportive Home Care',
  'Other'
];

const SchedulePage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, canCreateVisits } = useAuth();
  
  const [visits, setVisits] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [caregiverFilter, setCaregiverFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // For dropdowns
  const [clients, setClients] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  
  // Create dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newVisit, setNewVisit] = useState({
    client_id: '',
    caregiver_id: '',
    scheduled_start: '',
    scheduled_end: '',
    service_type: 'Personal Care',
    location_note: ''
  });
  
  // Assignment blocked error dialog
  const [blockedError, setBlockedError] = useState(null);

  const fetchVisits = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status_filter = statusFilter;
      if (clientFilter && clientFilter !== 'all') params.client_id = clientFilter;
      if (caregiverFilter && caregiverFilter !== 'all') params.caregiver_id = caregiverFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await visitsAPI.list(params);
      setVisits(response.data.visits);
      setTotal(response.data.total);
    } catch (err) {
      toast.error('Failed to load visits');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, clientFilter, caregiverFilter, dateFrom, dateTo]);

  const fetchDropdownData = async () => {
    try {
      // Get active clients
      const clientsRes = await clientsAPI.list({ status_filter: 'Active' });
      setClients(clientsRes.data.clients || []);
      
      // Get caregivers (users with Caregiver role)
      const usersRes = await usersAPI.list();
      const caregiverUsers = (usersRes.data.users || []).filter(u => u.role === 'Caregiver' && !u.is_archived);
      setCaregivers(caregiverUsers);
    } catch (err) {
      console.error('Failed to load dropdown data', err);
    }
  };

  useEffect(() => {
    fetchVisits();
    fetchDropdownData();
  }, [fetchVisits]);

  const handleCreateVisit = async () => {
    if (!newVisit.client_id || !newVisit.caregiver_id || !newVisit.scheduled_start || !newVisit.scheduled_end) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setCreateLoading(true);
    try {
      const data = {
        client_id: newVisit.client_id,
        caregiver_id: newVisit.caregiver_id,
        scheduled_start: new Date(newVisit.scheduled_start).toISOString(),
        scheduled_end: new Date(newVisit.scheduled_end).toISOString(),
        service_type: newVisit.service_type,
        location_note: newVisit.location_note || null
      };
      
      const response = await visitsAPI.create(data);
      toast.success('Visit scheduled successfully');
      setShowCreateDialog(false);
      setNewVisit({ client_id: '', caregiver_id: '', scheduled_start: '', scheduled_end: '', service_type: 'Personal Care', location_note: '' });
      navigate(`/visits/${response.data.id}`);
    } catch (err) {
      // Check if it's a caregiver compliance block error
      const detail = err.response?.data?.detail;
      if (err.response?.status === 400 && detail && typeof detail === 'object' && detail.blocked) {
        // Show blocked error dialog
        setBlockedError({
          ...detail,
          caregiver_name: caregivers.find(c => c.id === newVisit.caregiver_id)?.full_name || 'Selected caregiver',
          visit_date: newVisit.scheduled_start ? new Date(newVisit.scheduled_start).toLocaleDateString() : 'N/A'
        });
      } else if (err.response?.status === 400 && typeof detail === 'string' && detail.includes('not compliant')) {
        // Fallback for string error message about compliance
        toast.error(detail, { duration: 8000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to create visit');
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const formatDateTime = (dt) => {
    if (!dt) return '-';
    const d = new Date(dt);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  if (authLoading) {
    return (
      <Layout title="Schedule">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-600" />
        </div>
      </Layout>
    );
  }

  const userRole = user?.role || 'Unknown';
  const isCaregiver = userRole === 'Caregiver';

  return (
    <Layout title={isCaregiver ? 'My Schedule' : 'Schedule & Visits'}>
      <div className="space-y-6" data-testid="schedule-page">
        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading">Visits</CardTitle>
                <CardDescription>{total} total visits</CardDescription>
              </div>
              {canCreateVisits && (
                <Button onClick={() => setShowCreateDialog(true)} className="bg-avida-blue hover:bg-avida-blue-800" data-testid="create-visit-btn">
                  <Plus className="h-4 w-4 mr-2" />New Visit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-4">
              <div className="w-36">
                <Label>Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Scheduled">Scheduled</SelectItem>
                    <SelectItem value="InProgress">In Progress</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Cancelled">Cancelled</SelectItem>
                    <SelectItem value="NoShow">No Show</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {!isCaregiver && (
                <>
                  <div className="w-44">
                    <Label>Client</Label>
                    <Select value={clientFilter} onValueChange={setClientFilter}>
                      <SelectTrigger data-testid="client-filter">
                        <SelectValue placeholder="All clients" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Clients</SelectItem>
                        {clients.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.last_name}, {c.first_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-44">
                    <Label>Caregiver</Label>
                    <Select value={caregiverFilter} onValueChange={setCaregiverFilter}>
                      <SelectTrigger data-testid="caregiver-filter">
                        <SelectValue placeholder="All caregivers" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Caregivers</SelectItem>
                        {caregivers.map(cg => (
                          <SelectItem key={cg.id} value={cg.id}>{cg.last_name}, {cg.first_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div className="w-36">
                <Label>From</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from" />
              </div>
              <div className="w-36">
                <Label>To</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="date-to" />
              </div>
              <Button variant="outline" onClick={fetchVisits} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Visit List */}
        <Card data-testid="visit-list">
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : visits.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Calendar className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                <p>No visits found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Date/Time</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Client</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Caregiver</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Service</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Indicators</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visits.map((visit) => (
                      <tr 
                        key={visit.id} 
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/visits/${visit.id}`)}
                        data-testid={`visit-row-${visit.id}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-slate-400" />
                            <div>
                              <p className="text-sm font-medium">{formatDateTime(visit.scheduled_start)}</p>
                              <p className="text-xs text-slate-500">to {formatDateTime(visit.scheduled_end)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-slate-400" />
                            <span className="text-sm">{visit.client_name || visit.client_id}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {visit.caregiver_name || visit.caregiver_id}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {visit.service_type}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={STATUS_COLORS[visit.status] || 'bg-slate-100'}>
                            {visit.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {visit.has_note && (
                              <FileCheck className="h-4 w-4 text-green-600" title="Has note" />
                            )}
                            {visit.billable_flag && (
                              <DollarSign className="h-4 w-4 text-green-600" title="Billable" />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

      {/* Create Visit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Schedule New Visit</DialogTitle>
            <DialogDescription>Create a scheduled visit for a client</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Client *</Label>
              <Select value={newVisit.client_id} onValueChange={(v) => setNewVisit({...newVisit, client_id: v})}>
                <SelectTrigger data-testid="new-visit-client">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.last_name}, {c.first_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Only Active clients are shown</p>
            </div>
            <div>
              <Label>Caregiver *</Label>
              <Select value={newVisit.caregiver_id} onValueChange={(v) => setNewVisit({...newVisit, caregiver_id: v})}>
                <SelectTrigger data-testid="new-visit-caregiver">
                  <SelectValue placeholder="Select caregiver" />
                </SelectTrigger>
                <SelectContent>
                  {caregivers.map(cg => (
                    <SelectItem key={cg.id} value={cg.id}>{cg.last_name}, {cg.first_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time *</Label>
                <Input 
                  type="datetime-local"
                  value={newVisit.scheduled_start}
                  onChange={(e) => setNewVisit({...newVisit, scheduled_start: e.target.value})}
                  data-testid="new-visit-start"
                />
              </div>
              <div>
                <Label>End Time *</Label>
                <Input 
                  type="datetime-local"
                  value={newVisit.scheduled_end}
                  onChange={(e) => setNewVisit({...newVisit, scheduled_end: e.target.value})}
                  data-testid="new-visit-end"
                />
              </div>
            </div>
            <div>
              <Label>Service Type</Label>
              <Select value={newVisit.service_type} onValueChange={(v) => setNewVisit({...newVisit, service_type: v})}>
                <SelectTrigger data-testid="new-visit-service">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map(st => (
                    <SelectItem key={st} value={st}>{st}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location Note</Label>
              <Input 
                value={newVisit.location_note}
                onChange={(e) => setNewVisit({...newVisit, location_note: e.target.value})}
                placeholder="e.g., Client's home"
                data-testid="new-visit-location"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateVisit} disabled={createLoading} data-testid="submit-create-visit">
              {createLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Schedule Visit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment Blocked Error Dialog */}
      <Dialog open={!!blockedError} onOpenChange={() => setBlockedError(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-700">
              <AlertTriangle className="h-5 w-5" />
              Caregiver Assignment Blocked
            </DialogTitle>
            <DialogDescription>
              The selected caregiver cannot be assigned to this visit due to training compliance issues.
            </DialogDescription>
          </DialogHeader>
          {blockedError && (
            <div className="space-y-4 py-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="font-medium text-gray-900">{blockedError.caregiver_name}</p>
                <p className="text-sm text-gray-600">Visit Date: {blockedError.visit_date}</p>
              </div>

              {blockedError.missing_requirements && blockedError.missing_requirements.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-red-700 mb-2">Missing Trainings:</p>
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                    {blockedError.missing_requirements.map((req, i) => (
                      <li key={i}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}

              {blockedError.expired_requirements && blockedError.expired_requirements.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-red-700 mb-2">Expired Trainings:</p>
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                    {blockedError.expired_requirements.map((req, i) => (
                      <li key={i}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setBlockedError(null)}>
              Choose Different Caregiver
            </Button>
            {blockedError?.personnel_id && (
              <Button
                variant="default"
                className="bg-avida-blue hover:bg-avida-blue-800"
                onClick={() => {
                  const visitDate = newVisit.scheduled_start ? newVisit.scheduled_start.split('T')[0] : new Date().toISOString().split('T')[0];
                  window.open(`/personnel/${blockedError.personnel_id}?tab=compliance&date=${visitDate}`, '_blank');
                }}
                data-testid="view-personnel-compliance-btn"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View Personnel Compliance
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
};

export default SchedulePage;
